
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import PersonalInfoFields from './request-form/PersonalInfoFields';
import EmailFields from './request-form/EmailFields';
import QuestionFields from './request-form/QuestionFields';
import SubmitButton from './request-form/SubmitButton';
import { useEmailService } from '@/hooks/email/useEmailService';
import { useIntroText } from '@/hooks/email/useIntroText';
import { useTranslation } from 'react-i18next';

interface InterviewRequestFormProps {
  onSuccess?: (id: string) => void;
}

const InterviewRequestForm: React.FC<InterviewRequestFormProps> = ({ onSuccess }) => {
  const { t } = useTranslation();
  const { register, handleSubmit, formState: { errors }, reset } = useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { introText, updateIntroText, resetToDefault } = useIntroText();
  const { sendInterviewRequestEmail, isSendingEmail } = useEmailService();

  // Enhanced submit handler that uses only the email service
  const handleFormSubmit = async (data: any) => {
    console.log('Form data received:', data);
    console.log('Question field value:', data.question);
    console.log('Email field value:', data.intervieweeEmail);
    
    setIsSubmitting(true);
    try {
      // Send the email using the email service
      await sendInterviewRequestEmail({
        interviewee_name: data.intervieweeName,
        interviewee_email: data.intervieweeEmail,
        additional_emails: data.multipleEmails,
        question: data.question,
        context: data.context,
        personalized_message: data.personalizedMessage,
        email_subject: data.emailSubject || t('email.defaultSubject'),
        intro_text: introText
      });
      
      // Reset form and call success callback
      reset();
      if (onSuccess) {
        onSuccess("success"); // Just pass a success indicator
      }
    } catch (error) {
      console.error('Error in form submission:', error);
      // Error handling is already done in the email service hook
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-[#ea384c]">
      <h2 className="text-xl font-semibold mb-4">{t('interview.sendRequest')}</h2>
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
        {/* Personal information fields */}
        <PersonalInfoFields 
          register={register} 
          errors={errors} 
        />

        {/* Email related fields */}
        <EmailFields 
          register={register}
          introText={introText}
          updateIntroText={updateIntroText}
          resetToDefault={resetToDefault}
        />

        {/* Question fields */}
        <QuestionFields 
          register={register} 
          errors={errors} 
        />

        {/* Submit button */}
        <SubmitButton isSubmitting={isSubmitting || isSendingEmail} />
      </form>
    </div>
  );
};

export default InterviewRequestForm;
